import React from "react";
import Tooltip from "components/Tooltip";
import { useTranslation } from "react-i18next";
import { IconAlertTriangleFilled, IconBook } from "@tabler/icons-react";
import SimpleSelect from "components/SimpleSelect";
import { CIRCULARITY_GUIDE_URL } from "utils";
import { BPEStore, CustomProduct, Option } from "../ProductEditorStore";
import { observer } from "mobx-react-lite";

const Circularity = () => {
  const { t } = useTranslation();

  const showTooltip = (id: keyof CustomProduct) => {
    if (BPEStore.showTooltips?.[id]) {
      return (
        <Tooltip
          content={
            BPEStore.showTooltips?.[id] === "cpx_worst"
              ? t("productEditor.default_value_tooltip")
              : t(`productEditor.${id}_from_object_tooltip`)
          }
          iconClassName="!min-w-5 text-gray-500"
          className="absolute right-8 top-[28px]"
          icon={
            BPEStore.showTooltips?.[id] === "cpx_worst" ? (
              <IconAlertTriangleFilled size={20} className="text-yellow-500" />
            ) : undefined
          }
        />
      );
    }
  };

  return (
    <div className="py-6 border-b border-gray-200">
      <div className="text-base font-semibold mb-3 flex items-center">
        {t("productEditor.circularity")}
        <Tooltip
          content={t("productEditor.circularityTooltip")}
          className="ml-1 text-gray-500"
        ></Tooltip>
        <Tooltip content={t("productEditor.circularityGuideTooltip")}>
          <IconBook
            stroke={3}
            size={16}
            className="ml-2 text-gray-700 cursor-pointer hover:text-gray-900"
            onClick={() => window.open(CIRCULARITY_GUIDE_URL, "_blank")}
          />
        </Tooltip>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="w-full relative">
          <SimpleSelect
            options={BPEStore.optionsLists.preuse_qualification as Option[]}
            labelName={t("productEditor.material_origin")}
            placeholder={t("productEditor.selectValue")}
            name="preuse_qualification"
            onChange={(value, name) =>
              BPEStore.onChangeData(name as string, value)
            }
            current={BPEStore.data.preuse_qualification}
            containerClassName="w-full"
            required
            noMargin
          />
          {showTooltip("preuse_qualification")}
        </div>
        <div className="w-full relative">
          <SimpleSelect
            options={
              BPEStore.optionsLists.separability_qualification as Option[]
            }
            labelName={t("productEditor.separability")}
            placeholder={t("productEditor.selectValue")}
            containerClassName="w-full"
            name="separability_qualification"
            onChange={(value, name) =>
              BPEStore.onChangeData(name as string, value)
            }
            current={BPEStore.data.separability_qualification}
            required
            noMargin
          />
          {showTooltip("separability_qualification")}
        </div>
        <div className="w-full relative">
          <SimpleSelect
            options={
              BPEStore.optionsLists.deconstructability_qualification as Option[]
            }
            labelName={t("productEditor.deconstructability")}
            placeholder={t("productEditor.selectValue")}
            containerClassName="w-full"
            name="deconstructability_qualification"
            onChange={(value, name) =>
              BPEStore.onChangeData(name as string, value)
            }
            current={BPEStore.data.deconstructability_qualification}
            required
            noMargin
          />
          {showTooltip("deconstructability_qualification")}
        </div>
        <div className="w-full relative lg:col-span-full">
          <SimpleSelect
            options={
              BPEStore.optionsLists.reusability_qualification as Option[]
            }
            labelName={t("productEditor.reusability")}
            placeholder={t("productEditor.selectValue")}
            name="reusability_qualification"
            onChange={(value, name) =>
              BPEStore.onChangeData(name as string, value)
            }
            current={BPEStore.data.reusability_qualification}
            required
            noMargin
          />
          {showTooltip("reusability_qualification")}
        </div>
      </div>
    </div>
  );
};

export default observer(Circularity);
